import React, { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import type { ModalFooterOnClickHandler } from 'LuxKit/Modal/ModalFooter'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierThresholds from 'loyalty/selectors/getLoyaltyTierThresholds'
import { useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import TierProgressionAccordionItem from './TierProgressionAccordionItem'
import { BENEFIT_ICONS, TIER_PROGRESSION_BENEFIT_ORDER } from './TierProgressionBenefitTitle'

interface Props {
  tiers: Array<App.LoyaltyTier>
  defaultExpandedTier?: App.LoyaltyTier
  onBookNow?: () => void
  onAddToCart?: () => void
  checkoutDate?: string
}

function TierProgressionInfoModal({ tiers, defaultExpandedTier, onBookNow, onAddToCart, checkoutDate }: Props) {
  const intl = useIntl()
  const programDisplayName = getLoyaltyProgramDisplayName()
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)

  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const [loyaltyConfig] = useLoyaltyConfig()
  const [pendingPoints] = useCurrentLoyaltyPendingPoints()

  const [expandedTier, setExpandedTier] = useState<App.LoyaltyTier | null>(defaultExpandedTier ?? tiers[tiers.length - 1] ?? null)

  const hasActions = !!(onBookNow || onAddToCart)

  const handleBookNow = useCallback<ModalFooterOnClickHandler>((e, resolve) => {
    resolve()
    onBookNow?.()
  }, [onBookNow])

  const handleAddToCart = useCallback<ModalFooterOnClickHandler>((e, resolve) => {
    resolve()
    onAddToCart?.()
  }, [onAddToCart])

  const toggleTier = useCallback((tier: App.LoyaltyTier) => {
    setExpandedTier(prev => prev === tier ? null : tier)
  }, [])

  const getTierBenefits = useCallback((tier: App.LoyaltyTier) => {
    if (!loyaltyConfig?.regionTierBenefits) return []
    const benefits = loyaltyConfig.regionTierBenefits[tier] ?? []
    const filteredBenefits = benefits.filter(benefit =>
      BENEFIT_ICONS[benefit.code] !== null &&
      loyaltyConfig.regionEnabledBenefits[benefit.code],
    )
    return filteredBenefits.sort((a, b) => {
      const orderA = TIER_PROGRESSION_BENEFIT_ORDER.indexOf(a.code)
      const orderB = TIER_PROGRESSION_BENEFIT_ORDER.indexOf(b.code)
      const sortA = orderA === -1 ? TIER_PROGRESSION_BENEFIT_ORDER.length : orderA
      const sortB = orderB === -1 ? TIER_PROGRESSION_BENEFIT_ORDER.length : orderB
      return sortA - sortB
    })
  }, [loyaltyConfig])

  if (!tierThresholds) {
    return null
  }

  const currentStatusCredits = loyaltyAccount?.statusCredits ?? 0
  const currentNights = loyaltyAccount?.numberOfNights ?? 0
  const statusReviewDate = loyaltyAccount?.statusReviewDate

  const modalTitle = intl.formatMessage({
    id: 'Ppqq2OT1K5tZmAwITQNy',
    defaultMessage: '{programName} Status upgrade',
    description: 'Modal title for tier progression info in the loyalty program section',
  }, { programName: programDisplayName })

  const pendingCreditsTooltip = intl.formatMessage({
    id: 'Boqoor9d0GY6whbRZZHh',
    defaultMessage: 'Pending status credits may take up to 24hrs to appear',
    description: 'Tooltip explaining pending status credits delay in a modal dialog',
  })

  const pendingNightsTooltip = intl.formatMessage({
    id: 'Lc4cqOqiGPfrr0XCcytw',
    defaultMessage: 'Pending nights may take up to 24hrs to appear',
    description: 'Tooltip explaining pending nights delay in a modal dialog',
  })

  const showLessText = intl.formatMessage({
    id: 'm42k3kxkQ89vFkgg8pud',
    defaultMessage: '- less',
    description: 'Collapsible link text to hide additional benefits and show fewer items in the list',
  })

  const showMoreText = intl.formatMessage({
    id: 'Ol4tTKIoO42EEw3WM-WM',
    defaultMessage: '+ more',
    description: 'Expandable link text to reveal additional loyalty benefits in the breakdown list',
  })

  const isSingleTier = tiers.length === 1

  const bookNowText = (
    <FormattedMessage
      id="RXnnY1ws1cbaVNnOj246"
      defaultMessage="Book now"
      description="Book now button in tier progression modal in the loyalty section"
    />
  )

  const addToCartText = (
    <FormattedMessage
      id="hs0WtPgRbe6Y_Ho9ClTh"
      defaultMessage="Add to cart"
      description="Add to cart button in tier progression modal in the loyalty section"
    />
  )

  return (
    <Modal
      title={modalTitle}
      primaryActionText={hasActions ? bookNowText : undefined}
      secondaryActionText={hasActions ? addToCartText : undefined}
      onPrimaryActionClick={hasActions ? handleBookNow : undefined}
      onSecondaryActionClick={hasActions ? handleAddToCart : undefined}
    >
      <VerticalSpacer gap={16}>
        {tiers.map(tier => {
          const isExpanded = isSingleTier || expandedTier === tier
          const tierBenefits = getTierBenefits(tier)

          return (
            <TierProgressionAccordionItem
              key={tier}
              tier={tier}
              tierThresholds={tierThresholds}
              currentStatusCredits={currentStatusCredits}
              currentNights={currentNights}
              statusReviewDate={statusReviewDate}
              pendingPoints={pendingPoints}
              tierBenefits={tierBenefits}
              isExpanded={isExpanded}
              isSingleTier={isSingleTier}
              onToggle={toggleTier}
              pendingCreditsTooltip={pendingCreditsTooltip}
              pendingNightsTooltip={pendingNightsTooltip}
              showLessText={showLessText}
              showMoreText={showMoreText}
              checkoutDate={checkoutDate}
            />
          )
        })}
      </VerticalSpacer>
    </Modal>
  )
}

export default TierProgressionInfoModal
